import React, { useState } from 'react';
import { 
  Layers, Box, Shield, GitBranch, Settings, Search, Filter,
  CheckCircle2, AlertTriangle, ChevronRight, Code, Clock, Users,
  Package, Eye
} from 'lucide-react';
import { moduleCatalog, moduleTypes } from '../data/moduleCatalog';
import { useToast } from '../context/ToastContext';

const typeIcons = {
  terraform: Layers,
  helm: Box,
  config: Settings,
  policy: Shield,
  workflow: GitBranch
};

const typeColors = {
  terraform: { bg: 'bg-slate-200', text: 'text-slate-700' },
  helm: { bg: 'bg-primary/10', text: 'text-primary' },
  config: { bg: 'bg-primary/10', text: 'text-primary' },
  policy: { bg: 'bg-slate-100', text: 'text-slate-600' },
  workflow: { bg: 'bg-slate-200', text: 'text-slate-700' }
};

export const ModuleCatalog = () => {
  const { addToast } = useToast();
  const [selectedType, setSelectedType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedModule, setSelectedModule] = useState(null);

  const handleAction = (action) => {
    addToast(`Demo action: ${action}`, 'info');
  };

  const filteredModules = moduleCatalog.filter(m => {
    const matchesType = selectedType === 'all' || m.type === selectedType;
    const matchesSearch = m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          m.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesType && matchesSearch;
  });

  const renderModuleDetail = (module) => {
    const TypeIcon = typeIcons[module.type] || Layers;
    const colors = typeColors[module.type] || typeColors.terraform;
    
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 w-full max-w-2xl max-h-[90vh] overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${colors.bg} flex items-center justify-center`}>
                  <TypeIcon className={`w-5 h-5 ${colors.text}`} />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">{module.name}</h2>
                  <p className="text-sm text-gray-500">v{module.version}</p>
                </div>
              </div>
              <button onClick={() => setSelectedModule(null)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {/* Warning Banner */}
            <div className="mb-6 p-4 bg-slate-100 border border-slate-300 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-slate-700" />
                <span className="font-medium text-slate-800">Compose Only</span>
              </div>
              <p className="text-sm text-slate-700 mt-1">
                This module can only be used via Change Spec composition. Direct execution is not allowed.
              </p>
            </div>

            {/* Info */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Type</p>
                <p className="font-medium capitalize">{module.type}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Maintainer</p>
                <p className="font-medium">{module.maintainer}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Usage Count</p>
                <p className="font-medium">{module.usageCount}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Last Used</p>
                <p className="font-medium">{module.lastUsed}</p>
              </div>
            </div>

            <p className="text-gray-600 mb-6">{module.description}</p>

            {/* Parameters */}
            <h3 className="font-semibold mb-3">Parameters</h3>
            <div className="bg-gray-50 rounded-lg overflow-hidden mb-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left px-4 py-2 font-medium text-gray-500">Name</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-500">Type</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-500">Required</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-500">Constraints</th>
                  </tr>
                </thead>
                <tbody>
                  {module.parameters.map((param, idx) => (
                    <tr key={idx} className="border-b border-gray-100 last:border-0">
                      <td className="px-4 py-2 font-mono text-xs">{param.name}</td>
                      <td className="px-4 py-2 text-gray-600">{param.type}</td>
                      <td className="px-4 py-2">
                        {param.required ? (
                          <span className="text-slate-900 font-medium">Yes</span>
                        ) : (
                          <span className="text-gray-400">No</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-500">
                        {param.allowed && `Allowed: ${param.allowed.join(', ')}`}
                        {param.min !== undefined && `Min: ${param.min}`}
                        {param.max !== undefined && ` Max: ${param.max}`}
                        {param.default !== undefined && `Default: ${String(param.default)}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Example Usage */}
            <h3 className="font-semibold mb-3">Example Change Spec Reference</h3>
            <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
              <pre className="text-sm text-gray-100 font-mono">
{`{
  "moduleRef": "${module.id}",
  "moduleVersion": "${module.version}",
  "action": "create",
  "parameters": {
${module.parameters.filter(p => p.required).map(p => `    "${p.name}": "<value>"`).join(',\n')}
  }
}`}
              </pre>
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex gap-2">
            <button onClick={() => setSelectedModule(null)} className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-100">
              Close
            </button>
            <button onClick={() => handleAction(`View ${module.id} source`)} className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-100 flex items-center gap-2">
              <Code className="w-4 h-4" />
              View Source
            </button>
            <button onClick={() => handleAction(`Use ${module.id} in Change Spec`)} className="px-4 py-2 bg-[#4a70ff] text-white rounded-lg hover:bg-[#3a5fe6]">
              Use in Change Spec
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[1400px] mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-black mb-1">Module Catalog</h1>
          <p className="text-gray-500">Approved building blocks for GitOps composition</p>
        </div>

        {selectedModule && renderModuleDetail(selectedModule)}

        {/* Warning Banner */}
        <div className="mb-8 p-4 bg-primary/10 border border-primary/20 rounded-xl">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-primary" />
            <div>
              <span className="font-medium text-primary">Compose Only - No Direct Execution</span>
              <p className="text-sm text-primary/80 mt-0.5">
                All modules can only be referenced via Change Spec. The deterministic compiler validates all references against this catalog.
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search modules..."
              className="w-full h-10 pl-10 pr-4 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4a70ff]/20 focus:border-[#4a70ff]"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedType('all')}
              className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                selectedType === 'all'
                  ? 'bg-[#4a70ff] text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              All ({moduleCatalog.length})
            </button>
            {moduleTypes.map((type) => {
              const TypeIcon = typeIcons[type.id] || Layers;
              return (
                <button
                  key={type.id}
                  onClick={() => setSelectedType(type.id)}
                  className={`px-4 py-2 text-sm rounded-lg transition-colors flex items-center gap-2 ${
                    selectedType === type.id
                      ? 'bg-[#4a70ff] text-white'
                      : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <TypeIcon className="w-4 h-4" />
                  {type.label} ({type.count})
                </button>
              );
            })}
          </div>
        </div>

        {/* Module Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredModules.map((module) => {
            const TypeIcon = typeIcons[module.type] || Layers;
            const colors = typeColors[module.type] || typeColors.terraform;
            
            return (
              <div
                key={module.id}
                onClick={() => setSelectedModule(module)}
                className="bg-white rounded-xl border border-gray-200 p-5 hover:border-[#4a70ff]/30 hover:shadow-lg hover:shadow-[#4a70ff]/5 transition-all cursor-pointer"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-10 h-10 rounded-lg ${colors.bg} flex items-center justify-center`}>
                    <TypeIcon className={`w-5 h-5 ${colors.text}`} />
                  </div>
                  <div className="flex items-center gap-2">
                    {module.verified && (
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary rounded text-xs">
                        <CheckCircle2 className="w-3 h-3" />
                        Verified
                      </span>
                    )}
                    <code className="px-2 py-0.5 bg-gray-100 rounded text-xs">v{module.version}</code>
                  </div>
                </div>
                
                <h3 className="font-semibold text-black mb-1">{module.name}</h3>
                <p className="text-sm text-gray-500 mb-4 line-clamp-2">{module.description}</p>
                
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Users className="w-3.5 h-3.5" />
                    {module.maintainer}
                  </span>
                  <span className="flex items-center gap-1">
                    <Package className="w-3.5 h-3.5" />
                    {module.usageCount} uses
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {filteredModules.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No modules found matching your criteria</p>
          </div>
        )}
      </div>
    </div>
  );
};